/**
 * ProgressBar atom style mappings. Bar only; no label/tag/icon sizing.
 *
 * @internal Used by ProgressBar atom only.
 */

import type {
  ProgressBarIntent,
  ProgressBarPalette,
  ProgressBarSemantic,
  ProgressBarSize,
} from './ProgressBar.types';

/** Track and fill classes when overBackground (lighter contrast for dark/colored surfaces) */
export const overBackgroundStyles = {
  track: 'bg-white/20',
  fill: 'bg-white/60',
} as const;

/** Unfilled portion mask for brand gradient; aligns with track appearance */
export const unfilledOverlayStyles = {
  default: 'bg-dark-invirtus-soft',
  overBackground: 'bg-white/20',
} as const;

/** Semantic level derived from percent */
type SemanticLevel = 'success' | 'warning' | 'error';

const thresholds = { warning: 50, error: 80 } as const;

export function getSemanticLevel(percent: number, semantic: ProgressBarSemantic): SemanticLevel {
  const raw: SemanticLevel = percent >= thresholds.error ? 'error' : percent >= thresholds.warning ? 'warning' : 'success';
  if (semantic === 'completion') return raw === 'success' ? 'error' : raw === 'error' ? 'success' : 'warning';
  return raw;
}

/** Bar track height per size */
export const sizeStyles: Record<ProgressBarSize, string> = {
  xs: 'h-1',
  sm: 'h-1.5',
  md: 'h-2',
  lg: 'h-3',
} as const;

/** Standard palette: red, yellow, green */
const standardFillStyles: Record<string, string> = {
  primary: 'bg-purple',
  secondary: 'bg-dark-invirtus',
  info: 'bg-blue',
  success: 'bg-green',
  warning: 'bg-yellow',
  error: 'bg-red',
};

/** Brand palette: pink, light-lin, purple gradient */
const brandFillStyles: Record<string, string> = {
  primary: 'bg-purple',
  secondary: 'bg-dark-invirtus',
  info: 'bg-blue',
  success: 'bg-purple',
  warning: 'bg-light-lin',
  error: 'bg-brand-red',
};

/** Brand gradient colors */
const brandGradientColors = {
  start: 'var(--color-brand-red)',
  modLow: 'var(--color-gradient-pink)',
  center: 'var(--color-gradient-middle)',
  medHigh: 'var(--color-gradient-purple)',
  end: 'var(--color-purple)',
} as const;

const BRAND_TAG_CLASSES = [
  'bg-[var(--color-purple)]', 'bg-[var(--color-gradient-purple)]', 'bg-[var(--color-gradient-middle)]',
  'bg-[var(--color-gradient-pink)]', 'bg-[var(--color-brand-red)]',
] as const;

export function getBrandTagFillClass(percent: number): string {
  return BRAND_TAG_CLASSES[Math.min(4, Math.floor((percent / 100) * 5))];
}

export function getFloatingTagFillClass(percent: number, intent: ProgressBarIntent, palette: ProgressBarPalette): string {
  return palette === 'brand' ? getBrandTagFillClass(percent) : getFillClass(intent, palette);
}

export function getBrandFillGradient(semantic: ProgressBarSemantic): string {
  const { start, modLow, center, medHigh, end } = brandGradientColors;
  const [a, b, c, d, e] = semantic === 'usage' ? [end, medHigh, center, modLow, start] : [start, modLow, center, medHigh, end];
  return `linear-gradient(90deg, ${a} 0%, ${b} 25%, ${c} 50%, ${d} 75%, ${e} 100%)`;
}

/** Min distance from left/right edge for floating tag; keeps tag within track bounds */
export const floatingTagClampMin = '0.3rem';

/** Below this percent, tag is fixed on right to avoid overlapping label */
export const floatingTagLowPercentThreshold = 20;

export function getFillClass(intent: ProgressBarIntent, palette: ProgressBarPalette): string {
  const map = palette === 'brand' ? brandFillStyles : standardFillStyles;
  return map[intent] ?? map.primary;
}
